import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  CloseIcon,
  CloseListIcon,
  CrossCloseIcon,
  ListIcon,
  OpenIcon,
} from '@common/utils/icons/icons';
import TextInput from '@common/components/Inputs/TextInput';
import { useTranslation } from 'react-i18next';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { Theme, useMediaQuery } from '@mui/material';

export type WithOnClose<T = {}> = T & {
  onClose: () => void;
  list?: { open: boolean; onToggle: () => void };
};

interface CustomSlideInContentProps<T extends object> {
  open: boolean;
  list?: {
    title: string;
    items: { id: string; name: React.ReactNode }[];
    selectedItemId?: string;
    onClick: (id: string) => void;
  };
  search?: { searchFilter: string; onChange: (event: React.ChangeEvent<HTMLInputElement>) => void };
  ChildComponent: React.ComponentType<WithOnClose<T>>;
  childProps: T;
  pagination?: { nextClickHandler: (() => void) | null; prevClickHandler: (() => void) | null };
  onCloseOverlay: () => void;
}

const CustomSlideInContent = <T extends object>({
  open,
  list,
  search,
  onCloseOverlay,
  ChildComponent,
  childProps,
  pagination,
}: CustomSlideInContentProps<T>) => {
  const { t } = useTranslation();
  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [isSideListOpen, setIsSideListOpen] = useState(list != null);
  const [shouldRender, setShouldRender] = useState(true);

  const toggleSideListClickHandler = () => {
    if (list != null) {
      setIsSideListOpen((prevState) => !prevState);
    }
  };

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    } else {
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [open]);

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '100%',
          pt: 1.5,
          left: open ? 0 : 'calc(100% + 16px)',
          backgroundColor: theme.palette.background.paper,
          transition: 'left 0.3s ease-in-out',
          overflow: 'hidden',
          zIndex: theme.zIndex.fab - 1,
        })}
        direction="row"
      >
        {shouldRender && (
          <React.Fragment>
            <Box
              sx={{
                display: { xs: isSideListOpen ? 'inherit' : 'none', sm: 'block' },
                width: { xs: isSideListOpen ? '100%' : undefined, sm: 'min-content' },
              }}
            >
              <Stack
                sx={(theme) => ({
                  overflow: 'hidden',
                  transition: 'width 0.25s ease-in-out',
                  width: isSideListOpen ? { xs: '100%', sm: theme.spacing(32) } : theme.spacing(9),
                  pl: { sm: 2, xs: 1 },
                  height: '100%',
                })}
                spacing={2}
              >
                <Stack
                  spacing={1}
                  direction={isSideListOpen ? 'row' : 'column'}
                  justifyContent={isSideListOpen ? 'space-between' : undefined}
                  pr={isSideListOpen ? 2 : 0}
                >
                  <Stack spacing={1} direction="row" alignItems="center">
                    <TooltipIconButton
                      tooltip={t('info-tooltip-close')}
                      icon={<CrossCloseIcon />}
                      onClick={onCloseOverlay}
                      data-testid="close-overlay-button"
                    />
                    {isSideListOpen && list != null && (
                      <Typography variant="h2">{list.title}</Typography>
                    )}
                  </Stack>
                  {list != null &&
                    (isSideListOpen ? (
                      <TooltipIconButton
                        tooltipProps={{}}
                        tooltip={t('info-tooltip-close-sidebar')}
                        icon={<CloseListIcon data-testid="close-list-button" />}
                        onClick={toggleSideListClickHandler}
                      />
                    ) : (
                      <TooltipIconButton
                        tooltipProps={{}}
                        tooltip={t('info-tooltip-open-sidebar')}
                        icon={<ListIcon data-testid="open-list-button" />}
                        onClick={toggleSideListClickHandler}
                      />
                    ))}
                </Stack>
                {isSideListOpen && list != null && (
                  <React.Fragment>
                    {search != null && (
                      <Box pr={2}>
                        <TextInput
                          value={search.searchFilter}
                          size="small"
                          label={t('input-label-search') as string}
                          onChange={search.onChange}
                        />
                      </Box>
                    )}
                    <Box overflow="scroll" height="100%" pr={1}>
                      <Stack spacing={1}>
                        {list.items.map((i) => (
                          <Button
                            key={i.id}
                            onClick={() => {
                              list.onClick(i.id);
                              if (matchesSmDown) toggleSideListClickHandler();
                            }}
                            sx={(theme) => ({
                              justifyContent: 'left',
                              height: theme.spacing(6),
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              pl: 2,
                              backgroundColor:
                                i.id === list.selectedItemId ? theme.palette.grey[300] : undefined,
                              color:
                                i.id === list.selectedItemId
                                  ? theme.palette.grey[700]
                                  : theme.palette.action.active,
                              textTransform: 'none',
                            })}
                          >
                            {i.name}
                          </Button>
                        ))}
                        {pagination != null && (
                          <Stack direction="row" justifyContent="space-between" width="100%">
                            <IconButton
                              disabled={pagination.prevClickHandler == null}
                              onClick={pagination.prevClickHandler ?? undefined}
                            >
                              <CloseIcon />
                            </IconButton>
                            <IconButton
                              disabled={pagination.nextClickHandler == null}
                              onClick={pagination.nextClickHandler ?? undefined}
                            >
                              <OpenIcon />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </React.Fragment>
                )}
              </Stack>
            </Box>
            <Divider orientation="vertical" sx={{ display: { xs: 'none', sm: 'inherit' } }} />
            <Box
              flexGrow={1}
              sx={{
                display: { xs: isSideListOpen ? 'none' : undefined, sm: 'block' },
                overflow: 'auto',
                pb: 0.5,
                zIndex: 7,
                px: { xs: 0, sm: 2 },
              }}
            >
              {ChildComponent != null && (
                <ChildComponent
                  {...childProps}
                  onClose={onCloseOverlay}
                  list={{ open: isSideListOpen, onToggle: toggleSideListClickHandler }}
                />
              )}
            </Box>
          </React.Fragment>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default CustomSlideInContent;
